import { useState } from "react";
import { toast } from "sonner";
import { Crown } from "lucide-react";
import { Switch } from "../ui/switch";
import { useAuthedApi, errMsg, imgUrl } from "../../lib/api";
import { useSite } from "../../lib/site";
import ImageUploader from "../ImageUploader";
import BackButton from "../BackButton";
import BrandName, { BRAND_FONTS, BRAND_COLORS } from "../BrandName";

const BtnFields = ({ k, label, v, set }) => (
  <div className="lux-card p-4 space-y-3" data-testid={`branding-${k}-section`}>
    <p className="eyebrow !text-[10px]">{label}</p>
    <div className="grid sm:grid-cols-3 gap-3">
      <div><label className="lux-label">Button text</label><input className="lux-input" maxLength={30} value={v.text} onChange={(e) => set({ ...v, text: e.target.value })} data-testid={`branding-${k}-text`} /></div>
      <div><label className="lux-label">Button link</label><input className="lux-input" maxLength={300} value={v.link} onChange={(e) => set({ ...v, link: e.target.value })} placeholder="/invest or https://…" data-testid={`branding-${k}-link`} /></div>
      <div><label className="lux-label">Colour (blank = gold)</label>
        <div className="flex gap-2"><input type="color" className="w-10 h-9 rounded bg-transparent border border-white/10" value={v.color || "#D4AF37"} onChange={(e) => set({ ...v, color: e.target.value })} data-testid={`branding-${k}-color`} />
          <button type="button" className="text-xs text-gray-400 underline" onClick={() => set({ ...v, color: "" })} data-testid={`branding-${k}-color-reset`}>Gold</button></div></div>
    </div>
    <span className="btn-gold !py-1.5 !px-3 text-xs pointer-events-none" style={v.color ? { background: v.color } : {}}>{v.text || "Button"}</span>
  </div>
);

export default function BrandingForm({ section = "all", onDone }) {
  const api = useAuthedApi();
  const { branding, setBranding } = useSite();
  const [f, setF] = useState(branding);
  const [busy, setBusy] = useState(false);
  const show = (s) => section === "all" || section === s;

  const save = async () => {
    for (const k of ["advertise", "invest"]) if (f[k].link && !/^(\/\S*|https:\/\/\S+)$/.test(f[k].link)) return toast.error("Links must start with / or https://");
    setBusy(true);
    try { const r = await api("put", "/admin/settings/branding", f); setBranding(r); toast.success("Branding updated"); onDone?.(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="branding-form">
      {onDone && <BackButton onClick={onDone} testid="branding-back" />}
      {show("logo") && (
        <div className="lux-card p-4 space-y-3">
          <p className="eyebrow !text-[10px]">Logo & brand name</p>
          <div className="flex flex-wrap gap-4 items-start">
            <div className="w-28"><ImageUploader value={f.logo_url ? [f.logo_url] : []} onChange={(fn) => setF((p) => ({ ...p, logo_url: fn(p.logo_url ? [p.logo_url] : []).slice(-1)[0] || "" }))} max={1} testid="branding-logo" /></div>
            <div className="flex-1 min-w-[200px]"><label className="lux-label">Brand name</label><input className="lux-input" maxLength={40} value={f.brand_name} onChange={(e) => setF({ ...f, brand_name: e.target.value })} data-testid="branding-name" />
              <div className="flex items-center gap-2 mt-3" data-testid="branding-preview">
                {f.logo_url ? <img src={imgUrl(f.logo_url)} alt="" className="w-9 h-9 rounded-full object-cover" /> : <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--gold-grad)" }}><Crown className="w-4 h-4 text-black" /></span>}
                <BrandName name={f.brand_name} color={f.brand_color} font={f.brand_font} className="text-2xl" testid="branding-preview-name" />
              </div></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="lux-label">Brand name font</label>
              <select className="lux-select" value={f.brand_font || BRAND_FONTS[0]} onChange={(e) => setF({ ...f, brand_font: e.target.value })} data-testid="branding-font">
                {BRAND_FONTS.map((x) => <option key={x} value={x}>{x}</option>)}
              </select></div>
            <div><label className="lux-label">Brand name colour</label>
              <div className="flex flex-wrap items-center gap-1.5">
                {BRAND_COLORS.map(([c, l]) => (
                  <button key={l} type="button" title={l} onClick={() => setF({ ...f, brand_color: c })} data-testid={`branding-color-${l.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${(f.brand_color || "") === c ? "border-[#D4AF37] scale-110" : "border-white/20"}`}
                    style={{ background: c || "var(--gold-grad)" }} />
                ))}
                <input type="color" title="Custom colour" className="w-9 h-9 rounded bg-transparent border border-white/10" value={f.brand_color || "#D4AF37"} onChange={(e) => setF({ ...f, brand_color: e.target.value })} data-testid="branding-color-custom" />
              </div></div>
          </div>
        </div>
      )}
      {show("advertise") && <BtnFields k="advertise" label="Advertise With Us button" v={f.advertise} set={(v) => setF({ ...f, advertise: v })} />}
      {show("invest") && <BtnFields k="invest" label="Invest With Us button" v={f.invest} set={(v) => setF({ ...f, invest: v })} />}
      {section === "all" && (
        <label className="lux-card p-4 flex items-center justify-between gap-3 cursor-pointer">
          <span className="text-sm">Show edit pencils on the website (admin only)</span>
          <Switch className="data-[state=unchecked]:bg-gray-600" checked={f.pencils_on} onCheckedChange={(v) => setF({ ...f, pencils_on: v })} data-testid="branding-pencils-toggle" />
        </label>
      )}
      {section === "all" && (
        <div className="lux-card p-4 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-sm">Default theme for new visitors<span className="block text-xs text-gray-500">Customers can still switch with the sun/moon icon</span></span>
          <select className="lux-select !w-48" value={f.default_theme || "dark"} onChange={(e) => setF({ ...f, default_theme: e.target.value })} data-testid="branding-default-theme">
            <option value="dark">Dark (black & gold)</option><option value="light">Light (white & gold)</option><option value="auto">Auto (device setting)</option>
          </select>
        </div>
      )}
      <div className="flex justify-end"><button className="btn-gold" disabled={busy} onClick={save} data-testid="branding-save">{busy ? "Saving..." : "Save changes"}</button></div>
    </div>
  );
}
